import React from "react";
import _ from "underscore";
import * as ReactDOM from "react-dom";
import { Helmet } from "react-helmet";
import classNames from "classnames";
import { Link } from "react-router";
import { Form, Button, TextInput, CheckboxInput } from "armstrong-react";

import { FlowBox } from "../../../core/atoms/atoms";
import { Logo } from "../../../core/atoms";

import "./resetPasswordForm.scss";

interface Props {
  setPassword: (pass: string) => Promise<any>;
  routeToLogin: string;
}

interface State {
  password?: string;
  confirmation?: string;
  errorMessage?: string;
  visiblePasswords?: boolean;
  passwordsMatch?: boolean;
}

export default class ResetPasswordForm extends React.Component<Props, State>{
  constructor(props: Props) {
    super(props);
    this.state = {
      passwordsMatch: true
    };
  }

  private onChange = () => {
    // success feedback when user is correcting typo in confirmation
    if (!this.state.passwordsMatch) {
      this.setState({ passwordsMatch: this.state.password === this.state.confirmation }, () => this.forceUpdate());
    }
    else {
      this.forceUpdate();
    }
  }

  private setPassword = async () => {
    if (!this.state.password || !this.state.confirmation) {
      this.setState({ errorMessage: "you haven't typed a password into both boxes" });
      return;
    }

    if (this.state.password !== this.state.confirmation) {
      this.setState({ errorMessage: "The passwords you've typed are different - please type the same password twice." });
      return;
    }

    try {
      await this.props.setPassword(this.state.password);
    }
    catch(e) {
      let err = e as IFetchApiFailure;
      this.setState({ errorMessage: err.message });
      }
      }

  private passwordsMatch = () => this.state.password && this.state.password.length > 1 && this.state.password === this.state.confirmation;

  render() {
    // Invalidness state is independent of form submittability
    let canSubmit = this.passwordsMatch();

    return (
      <div className="reset-password">
        <Helmet title="Reset your TravelLocal password" />

        <div className="wrap">
          <Logo type="accent" link={this.props.routeToLogin} width={96} />

          <p className="subtitle">Step 3/3</p>
          <h1>Reset your password</h1>
          <p>Enter your new password in each of the boxes below.</p>

          <hr/>

            <Form dataBinder={Form.jsonDataBinder(this.state)} onDataChanged={() => this.onChange()}>
              {this.state.errorMessage &&
              <div className="validation-bar">There was a problem: {this.state.errorMessage}.</div>
              }

              <div className="textbox">
                <label>Your password</label>
                <TextInput {...(this.state.visiblePasswords ? Form.Bind.text("password") : Form.Bind.password("password"))} placeholder="******" />
              </div>

              <div className={classNames("textbox", { "invalid": !this.state.passwordsMatch })}>
                <label>{this.state.passwordsMatch ? "Password confirmation" : "Please type the same password again:"}</label>
                <TextInput {...(this.state.visiblePasswords ? Form.Bind.text("confirmation") : Form.Bind.password("confirmation"))}
                            placeholder="**********"
                            onBlur={() => this.setState({ passwordsMatch: this.passwordsMatch() })} />
              </div>

              <FlowBox>
                <CheckboxInput {...Form.Bind.checkbox("visiblePasswords")} className="make-password-visible" labelContent="Show passwords" />
              <Button disabled={this.state.password && this.state.password !== this.state.confirmation} onClick={() => this.setPassword()} type="submit" leftIcon={Button.Icomoon.checkmark}>Change my password</Button>
              </FlowBox>
            </Form>

          <hr />

          <h2>Tips for choosing a password</h2>

          <p><strong>Make it memorable.</strong> The best password is one that only you know! But there's no point choosing a password you're just going to forget. Got any favourite travel moments?</p>

          <p><strong>Make it long.</strong> The longer your password is, the longer it will take for someone to break it. An easy way to increase the length of your password is to use a phrase - choose a few words, stick them together, and you've got yourself a really long password.</p>

          <p><strong>Make it special.</strong> It's important to use a password that you don't use with other websites. This is the single most important thing you can do to protect yourself from password breaches, because it limits how much information can be released by each one.</p>

          <hr/>

          <p>If you're having difficulties, call us on +44 (0)117 325 7898 or email us at <a href="mailto:team@travellocal.com">team@travellocal.com</a></p>

          <p className="m-top-medium">Changed your mind? Remembered your password? <Link to={this.props.routeToLogin}>Click here to go back to the login page.</Link></p>
        </div>
        <div className="image-bg" />
      </div>
    );
  }
}
